import { RoleEntity } from '@do/backend-entities';
import { Precision } from '@do/common-interfaces';
import { TranslatedError } from '@do/common-interfaces';
import { marker } from '@do/ngx-translate-extract-marker';

import { RoleCode } from './entity-constants';
import { SeatControlUnitStatusDto } from '@do/common-dto';

export const replaceAll = (str: string, find: string, replace: string) => {
  return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
};

const escapeRegExp = (str: string) => {
  return str.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};
export function sleep(delay: number) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}
export const kebabize = (str: string) =>
  str.replace(
    /[A-Z]+(?![a-z])|[A-Z]/g,
    ($, ofs) => (ofs ? '-' : '') + $.toLowerCase()
  );

export const camelize = (s: string) =>
  s.replace(/-./g, (x) => x[1].toUpperCase());

export const passwordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*_-])(?=.{8,})/;

export const canViewAll = (role: RoleEntity | undefined): boolean =>
  role?.code == RoleCode.SuperAdmin || role?.code == RoleCode.Admin;

export const hasPermission = (
  permission: string,
  entity: string,
  permissions?: string[]
) => {
  const entityName = kebabize(entity.replace('Entity', '')).replace(/-/gi, '_');

  if (!permissions) {
    return false;
  }

  if (permissions?.indexOf(`${permission}_all`) > -1) {
    return true;
  } else {
    return permissions.indexOf(`${permission}_${entityName}_entity`) > -1;
  }
};

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export const decimalFormatter = (precision: Precision): Intl.NumberFormat =>
  new Intl.NumberFormat('it-IT', {
    style: 'decimal',
    minimumFractionDigits: precision.minimumFractionDigits,
    maximumFractionDigits: precision.maximumFractionDigits,
  });

export const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

export const isImage = (mimeType: string) => {
  return mimeType?.startsWith('image') || false;
};

export const getDescendantProp = (obj: any, path: string) => {
  const arr = path.split('.');
  while (arr.length && (obj = obj[arr.shift() as string]));
  return obj;
};

export const genCharArray = (charFrom: string, charTo: string) => {
  const a: string[] = [];
  let i = charFrom.charCodeAt(0);
  for (; i <= charTo.charCodeAt(0); ++i) {
    a.push(String.fromCharCode(i));
  }
  return a;
};

export const getErrorDescription = (
  byteCode: number | null
): TranslatedError => {
  if (byteCode === null || byteCode === undefined) {
    return { key: 'ERROR_MESSAGE', params: { peripheral: '', error: '' } };
  }

  /*
  0	No Error
  1	Switch
  2	Seat Number
  3	Aisle Light 1
  4	Aisle Light 2
  5	Aisle Light 3
  7	Table Light
  8	Under Chair LED
  9	Motor 1
  10	Motor 2
  11	Motor 3
  12	Heating Pad
  13	Occupancy Sensor
  14	Air Massage
  15	Generic
  */
  const peripheralCode = Math.floor(byteCode / 16);

  let peripheralKey: string;
  if (peripheralCode === 0) {
    peripheralKey = '';
  } else if (peripheralCode === 1) {
    peripheralKey = marker('Switch');
  } else if (peripheralCode === 2) {
    peripheralKey = marker('Seat number');
  } else if (peripheralCode === 3) {
    peripheralKey = marker('Aisle light 1');
  } else if (peripheralCode === 4) {
    peripheralKey = marker('Aisle light 2');
  } else if (peripheralCode === 5) {
    peripheralKey = marker('Aisle light 3');
  } else if (peripheralCode === 7) {
    peripheralKey = marker('Table light');
  } else if (peripheralCode === 8) {
    peripheralKey = marker('Under chair LED');
  } else if (peripheralCode === 9) {
    peripheralKey = marker('Motor 1');
  } else if (peripheralCode === 10) {
    peripheralKey = marker('Motor 2');
  } else if (peripheralCode === 11) {
    peripheralKey = marker('Motor 3');
  } else if (peripheralCode === 12) {
    peripheralKey = marker('Heating pad');
  } else if (peripheralCode === 13) {
    peripheralKey = marker('Occupancy sensor');
  } else if (peripheralCode === 14) {
    peripheralKey = marker('Air massage');
  } else if (peripheralCode === 15) {
    peripheralKey = marker('Generic');
  } else {
    peripheralKey = marker('Unknown error');
  }

  /*
  0	No Error
  1	Not connected
  2	Wrong consumption
  3	Current Overload
  4	Button stucked
  */
  const errorCode = byteCode % 16;
  let errorKey: string;

  if (errorCode === 0) {
    errorKey = '';
  } else if (errorCode === 1) {
    errorKey = marker('Not connected');
  } else if (errorCode === 2) {
    errorKey = marker('Wrong consumption');
  } else if (errorCode === 3) {
    errorKey = marker('Current overload');
  } else if (errorCode === 4) {
    errorKey = marker('Button stucked');
  } else {
    errorKey = marker('Unknown error');
  }

  return {
    key: 'ERROR_MESSAGE',
    params: {
      peripheral: peripheralKey,
      error: errorKey,
    },
  };
};

    export const errorDescription: (seat: SeatControlUnitStatusDto) => string = (seat: SeatControlUnitStatusDto) =>  {
    const description = getErrorDescription(seat.errorCode);
    return `${description.params['peripheral']} ${description.params['error']}`;
    }
